import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Avatar,
  Stack,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { UserProfile, ProfileUpdateData } from '../../types/settings.types';
import { settingsService } from '../../services/settings.service';
import toast from 'react-hot-toast';

interface ProfileSettingsProps {
  profile: UserProfile;
  onUpdate: (profile: UserProfile) => void;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({ profile, onUpdate }) => {
  const [formData, setFormData] = useState<ProfileUpdateData>({
    full_name: profile.full_name || '',
    phone: profile.phone || '',
    department: profile.department || '',
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field: keyof ProfileUpdateData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setIsUploading(true);
    try {
      const avatarUrl = await settingsService.uploadAvatar(profile.id, file);
      const updated = await settingsService.updateProfile(profile.id, { avatar_url: avatarUrl });
      onUpdate(updated);
      toast.success('Avatar updated successfully');
    } catch (error) {
      toast.error('Failed to upload avatar');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await settingsService.updateProfile(profile.id, formData);
      onUpdate(updated);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Box>
      <Stack direction='row' spacing={3} sx={{ mb: 3, alignItems: 'center' }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={profile.avatar_url}
            sx={{ width: 100, height: 100 }}
          >
            {profile.full_name?.charAt(0) || profile.email.charAt(0)}
          </Avatar>
          <IconButton
            component='label'
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'background.paper' },
            }}
            disabled={isUploading}
          >
            <input
              hidden
              accept='image/*'
              type='file'
              onChange={handleAvatarUpload}
            />
            <PhotoCameraIcon />
          </IconButton>
        </Box>
        <Box>
          <Typography variant='h6'>{profile.full_name || 'No name set'}</Typography>
          <Typography variant='body2' color='text.secondary'>
            {profile.email}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Role: {profile.role}
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2}>
        {/* Personal Information Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>Personal Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label='Full Name'
                value={formData.full_name}
                onChange={handleChange('full_name')}
              />
              <TextField
                fullWidth
                label='Email'
                value={profile.email}
                disabled
                helperText='Email cannot be changed'
              />
              <TextField
                fullWidth
                label='Phone'
                value={formData.phone}
                onChange={handleChange('phone')}
              />
              <TextField
                fullWidth
                label='Department'
                value={formData.department}
                onChange={handleChange('department')}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Account Information Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>Account Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label='User ID'
                value={profile.id}
                disabled
                helperText='System-generated unique identifier'
              />
              <TextField
                fullWidth
                label='System Role'
                value={profile.role}
                disabled
                helperText='System-level access role'
              />
              <TextField
                fullWidth
                label='Organization Role'
                value={profile.org_role || 'Not assigned'}
                disabled
                helperText='Role within your organization'
              />
              <TextField
                fullWidth
                label='Organization'
                value={profile.organization_name || 'No organization'}
                disabled
                helperText='Your organization name'
              />
              <TextField
                fullWidth
                label='Account Status'
                value={profile.is_active ? 'Active' : 'Inactive'}
                disabled
                helperText='Current account status'
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Timestamps Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>Timestamps</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label='Account Created'
                value={formatDate(profile.created_at)}
                disabled
                helperText='When your account was created'
              />
              <TextField
                fullWidth
                label='Last Updated'
                value={formatDate(profile.updated_at)}
                disabled
                helperText='Last profile update time'
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Save Button */}
        <Box sx={{ pt: 2 }}>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={isSaving}
            size='large'
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};